import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  ArrowCounterClockwise,
  CheckCircle,
  Plus,
  RocketLaunch,
  ShieldCheck,
} from "@phosphor-icons/react";
import { useAuth } from "../../app/auth";
import { Drawer, MetricStrip, Modal, StatusTag } from "../../components/Primitives";
import { QueryErrorState } from "../../components/States";
import { useToast } from "../../components/Toast";
import { formatDateTime } from "../../lib/format";
import { useApiQuery } from "../../lib/query";
import type { DeviceGroup, OperationPolicy } from "../../lib/types";

const policyTypeLabels: Record<string, string> = {
  device: "设备运行策略",
  task: "任务执行策略",
  alert: "告警处置策略",
};

function PolicyEditor({
  policy,
  onClose,
}: {
  policy?: OperationPolicy;
  onClose(): void;
}) {
  const { request } = useAuth();
  const queryClient = useQueryClient();
  const notify = useToast();
  const [name, setName] = useState(policy?.name || "");
  const [policyType, setPolicyType] = useState(policy?.policyType || "task");
  const [description, setDescription] = useState(policy?.description || "");
  const [maxConcurrentTasks, setMaxConcurrentTasks] = useState(
    String(policy?.config.maxConcurrentTasks || 10),
  );
  const [requiresApproval, setRequiresApproval] = useState(
    Boolean(policy?.config.requiresApproval),
  );
  const [offlineThresholdMinutes, setOfflineThresholdMinutes] = useState(
    String(policy?.config.offlineThresholdMinutes || 15),
  );
  const config =
    policyType === "task"
      ? { maxConcurrentTasks: Number(maxConcurrentTasks), requiresApproval }
      : policyType === "device"
        ? { offlineThresholdMinutes: Number(offlineThresholdMinutes) }
        : { autoNotify: true, escalationMinutes: 30 };
  const mutation = useMutation({
    mutationFn: () =>
      request(policy ? `/api/policies/${policy.id}` : "/api/policies", {
        method: policy ? "PATCH" : "POST",
        body: JSON.stringify({ name, policyType, description, config }),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["policies"] });
      notify(policy ? "策略草稿已更新" : "策略已创建", name);
      onClose();
    },
  });
  return (
    <Modal title={policy ? "编辑策略草稿" : "创建运行策略"} description="Connect 策略只影响设备、任务和告警执行层。" onClose={onClose}
      footer={<><button className="secondary-button compact" onClick={onClose}>取消</button><button className="primary-button" disabled={!name.trim() || mutation.isPending} onClick={() => mutation.mutate()}>{mutation.isPending ? "正在保存…" : "保存草稿"}</button></>}>
      <div className="form-stack">
        <label><span>策略名称</span><input type="text" value={name} onChange={(event) => setName(event.target.value)} /></label>
        {!policy && <label><span>策略类型</span><select value={policyType} onChange={(event) => setPolicyType(event.target.value)}>{Object.entries(policyTypeLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label>}
        <label><span>说明</span><textarea rows={2} value={description} onChange={(event) => setDescription(event.target.value)} /></label>
        {policyType === "task" && (
          <>
            <label><span>最大并发任务数</span><input type="text" value={maxConcurrentTasks} onChange={(event) => setMaxConcurrentTasks(event.target.value)} /></label>
            <label className="check-row"><input type="checkbox" checked={requiresApproval} onChange={(event) => setRequiresApproval(event.target.checked)} /><span><strong>默认要求审批</strong><small>高风险任务进入审批队列后再执行。</small></span></label>
          </>
        )}
        {policyType === "device" && <label><span>离线告警阈值（分钟）</span><input type="text" value={offlineThresholdMinutes} onChange={(event) => setOfflineThresholdMinutes(event.target.value)} /></label>}
        {mutation.isError && <p className="form-error">策略保存失败，请检查名称和配置值。</p>}
      </div>
    </Modal>
  );
}

function PolicyDetail({ policy, groups, onClose }: { policy: OperationPolicy; groups: DeviceGroup[]; onClose(): void }) {
  const { request } = useAuth();
  const queryClient = useQueryClient();
  const notify = useToast();
  const [validation, setValidation] = useState<{ valid: boolean; errors: Array<{ field: string; message: string }> } | null>(null);
  const [changeNote, setChangeNote] = useState("");
  const [groupId, setGroupId] = useState(groups[0]?.id || "");
  const assignmentsQuery = useApiQuery<Array<{ id: string; groupId: string; groupName: string; version: number }>>(
    ["policy-assignments", policy.id],
    `/api/policies/${policy.id}/assignments`,
  );
  const validate = useMutation({
    mutationFn: () => request<{ valid: boolean; errors: Array<{ field: string; message: string }> }>(`/api/policies/${policy.id}/validate`, { method: "POST" }),
    onSuccess: (result) => {
      setValidation(result);
      notify(result.valid ? "策略校验通过" : "策略校验未通过");
    },
  });
  const publish = useMutation({
    mutationFn: () => request(`/api/policies/${policy.id}/publish`, { method: "POST", body: JSON.stringify({ changeNote }) }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["policies"] });
      notify("策略已发布", `${policy.name} · 新版本已生效`);
      onClose();
    },
  });
  const rollback = useMutation({
    mutationFn: (version: number) => request(`/api/policies/${policy.id}/rollback`, { method: "POST", body: JSON.stringify({ version }) }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["policies"] });
      notify("已生成回滚草稿", policy.name);
      onClose();
    },
  });
  const assign = useMutation({
    mutationFn: () => request(`/api/policies/${policy.id}/assignments`, {
      method: "POST",
      body: JSON.stringify({ groupIds: [groupId] }),
    }),
    onSuccess: async () => {
      await assignmentsQuery.refetch();
      notify("策略已分配", `v${policy.version} 已应用到设备组`);
    },
  });
  return (
    <Drawer title={policy.name} subtitle={`${policyTypeLabels[policy.policyType]} · 当前 v${policy.version}`} onClose={onClose}>
      <div className="policy-detail-head"><ShieldCheck size={28} /><span><strong>{policy.description || "未填写策略说明"}</strong><small>更新于 {formatDateTime(policy.updatedAt)}</small></span><StatusTag value={policy.status} /></div>
      <section className="drawer-section"><h3>当前草稿配置</h3><pre className="json-preview">{JSON.stringify(policy.config, null, 2)}</pre></section>
      <section className="drawer-section policy-actions">
        <button className="secondary-button compact" disabled={validate.isPending} onClick={() => validate.mutate()}><CheckCircle size={16} />校验草稿</button>
        <label><span>发布说明</span><input value={changeNote} onChange={(event) => setChangeNote(event.target.value)} placeholder="本次变更内容" /></label>
        <button className="primary-button" disabled={publish.isPending || !changeNote.trim() || validation?.valid !== true} onClick={() => publish.mutate()}><RocketLaunch size={16} />发布新版本</button>
        {validation && !validation.valid && <div className="validation-errors">{validation.errors.map((error) => <p key={error.field}>{error.field}：{error.message}</p>)}</div>}
      </section>
      <section className="drawer-section"><h3>历史版本</h3><div className="version-list">{(policy.versions || []).map((version) => <div key={version.id}><span><strong>v{version.version}</strong><small>{version.changeNote || "无版本说明"} · {formatDateTime(version.createdAt)}</small></span>{version.version !== policy.version && <button title="回滚为草稿" disabled={rollback.isPending} onClick={() => rollback.mutate(version.version)}><ArrowCounterClockwise size={15} /></button>}</div>)}</div></section>
      <section className="drawer-section">
        <h3>设备组分配</h3>
        <div className="form-stack">
          <label><span>目标设备组</span><select value={groupId} onChange={(event) => setGroupId(event.target.value)}><option value="">请选择设备组</option>{groups.map((group) => <option key={group.id} value={group.id}>{group.name}（{group.memberCount} 台）</option>)}</select></label>
          <button className="secondary-button compact" disabled={policy.status !== "published" || !groupId || assign.isPending} onClick={() => assign.mutate()}>分配当前版本</button>
          <div className="version-list">{(assignmentsQuery.data || []).map((assignment) => <div key={assignment.id}><span><strong>{assignment.groupName}</strong><small>正在使用 v{assignment.version}</small></span><StatusTag value="生效中" tone="green" /></div>)}</div>
          {!groups.length && <p className="inline-empty">请先在设备管理中创建设备组。</p>}
          {assign.isError && <p className="form-error">分配失败，策略必须先发布。</p>}
        </div>
      </section>
    </Drawer>
  );
}

export function PoliciesPage() {
  const policiesQuery = useApiQuery<OperationPolicy[]>(["policies"], "/api/policies");
  const groupsQuery = useApiQuery<DeviceGroup[]>(["device-groups"], "/api/device-groups");
  const [editing, setEditing] = useState<OperationPolicy | "new" | null>(null);
  const [selected, setSelected] = useState<OperationPolicy | null>(null);
  const policies = policiesQuery.data || [];
  if (policiesQuery.isError || groupsQuery.isError) return <QueryErrorState onRetry={() => { void policiesQuery.refetch(); void groupsQuery.refetch(); }} />;
  return (
    <div className="module-page">
      <MetricStrip metrics={[
        { label: "策略总数", value: policies.length, note: "执行层策略" },
        { label: "已发布", value: policies.filter((policy) => policy.status === "published").length, note: "正在生效", tone: "metric-green" },
        { label: "草稿", value: policies.filter((policy) => policy.status === "draft").length, note: "尚未发布" },
        { label: "策略类型", value: new Set(policies.map((policy) => policy.policyType)).size, note: "设备、任务与告警" },
      ]} />
      <section className="table-panel">
        <header className="module-toolbar"><div><h2 className="toolbar-title">运行策略</h2><p className="toolbar-subtitle">草稿校验通过后才能发布，历史版本保持不可变。</p></div><button className="primary-button" onClick={() => setEditing("new")}><Plus size={16} />创建策略</button></header>
        <div className="policy-grid">
          {policies.map((policy) => <article key={policy.id} onClick={() => setSelected(policy)}><header><span className={`policy-icon ${policy.policyType}`}><ShieldCheck size={22} /></span><StatusTag value={policy.status} /></header><h3>{policy.name}</h3><p>{policy.description || "未填写策略说明"}</p><div><span>{policyTypeLabels[policy.policyType]}</span><strong>v{policy.version}</strong></div><footer><small>更新于 {formatDateTime(policy.updatedAt)}</small><button className="text-button" onClick={(event) => { event.stopPropagation(); setEditing(policy); }}>编辑草稿</button></footer></article>)}
          {!policiesQuery.isPending && !policies.length && <div className="empty-grid-state">尚未创建运行策略。</div>}
        </div>
      </section>
      {editing && <PolicyEditor policy={editing === "new" ? undefined : editing} onClose={() => setEditing(null)} />}
      {selected && <PolicyDetail policy={selected} groups={groupsQuery.data || []} onClose={() => setSelected(null)} />}
    </div>
  );
}
